/**
 * AudioEngine - Web Audio API wrapper for Simon Revolution
 * Handles tone synthesis with positional stereo panning
 */

export type ButtonColor = 'red' | 'green' | 'blue' | 'yellow';

// Classic Simon-like tone mapping for distinct, recognizable notes
const COLOR_FREQUENCIES: Record<ButtonColor, number> = {
  green: 164.81,  // E3 - Deep, grounding bass note
  red: 220.00,    // A3 - Warm middle tone
  yellow: 277.18, // C#4 - Bright, sharp accent
  blue: 329.63    // E4 - High, clear treble
};

export class AudioEngine {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private isInitialized = false;
  private kickScheduleIds: number[] = [];
  private snareScheduleIds: number[] = [];

  /**
   * Initialize the AudioContext
   * Must be called from a user gesture (tap/click)
   */
  async init(): Promise<void> {
    if (this.isInitialized) return;

    try {
      this.context = new AudioContext();
      this.masterGain = this.context.createGain();
      this.masterGain.gain.value = 0.5; // Increased master volume for better audibility
      this.masterGain.connect(this.context.destination);
      
      // Resume if suspended (some browsers suspend on init)
      if (this.context.state === 'suspended') {
        await this.context.resume();
      }

      this.isInitialized = true;
      console.log('AudioEngine initialized successfully');

      // Warm-up chirp to unlock audio reliably on mobile Safari/Chrome.
      this.playTone('green', 0, 1, 120);
    } catch (err) {
      console.error('Failed to initialize AudioContext:', err);
      throw err;
    }
  }

  /**
   * Resume AudioContext if suspended
   * Useful when returning from background
   */
  async resume(): Promise<void> {
    if (this.context && this.context.state === 'suspended') {
      await this.context.resume();
    }
  }

  /**
   * Play a tone with positional stereo panning
   * @param color - Button color determining frequency
   * @param posX - X position of button on screen
   * @param screenWidth - Total screen width for pan calculation
   * @param durationMs - Duration of the tone in milliseconds
   */
  playTone(
    color: ButtonColor,
    posX: number,
    screenWidth: number,
    durationMs: number = 300
  ): void {
    if (!this.context || !this.masterGain) {
      console.warn('AudioEngine not initialized');
      return;
    }

    if (this.context.state === 'suspended') {
      this.context.resume().catch(() => {});
    }

    const frequency = COLOR_FREQUENCIES[color];
    const now = this.context.currentTime;

    // Create oscillator
    const oscillator = this.context.createOscillator();
    oscillator.type = 'triangle';
    oscillator.frequency.value = frequency;

    // Create gain for envelope with improved audibility
    const gainNode = this.context.createGain();
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.75, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.12, now + durationMs / 1000);

    // Create stereo panner
    const panner = this.context.createStereoPanner();
    // Calculate pan: -1 (left) to 1 (right)
    const pan = Math.max(-1, Math.min(1, (posX / screenWidth) * 2 - 1));
    panner.pan.value = pan;

    // Connect nodes: oscillator → gain → panner → master → destination
    oscillator.connect(gainNode);
    gainNode.connect(panner);
    panner.connect(this.masterGain);

    // Start and stop
    oscillator.start(now);
    oscillator.stop(now + durationMs / 1000);

    // Clean up
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
      panner.disconnect();
    };
  }

  /**
   * Check if audio is ready
   */
  get ready(): boolean {
    return this.isInitialized && this.context !== null;
  }

  /**
   * Play kick (bombo) sound - deep bass drum
   * Frequency: 80-100 Hz, short duration
   */
  playKick(): void {
    if (!this.context || !this.masterGain) {
      console.warn('AudioEngine not initialized');
      return;
    }

    if (this.context.state === 'suspended') {
      this.context.resume().catch(() => {});
    }

    const now = this.context.currentTime;

    // Create oscillator for kick
    const oscillator = this.context.createOscillator();
    oscillator.type = 'sine';
    
    // Pitch bend from 150Hz to 40Hz for punch
    oscillator.frequency.setValueAtTime(150, now);
    oscillator.frequency.exponentialRampToValueAtTime(40, now + 0.05);

    // Create gain envelope
    const gainNode = this.context.createGain();
    gainNode.gain.setValueAtTime(0.8, now);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    // Connect and play
    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain);
    oscillator.start(now);
    oscillator.stop(now + 0.15);

    // Clean up
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  }

  /**
   * Play count-in kick (louder and more pronounced than regular kick)
   */
  playCountInKick(): void {
    if (!this.context || !this.masterGain) {
      console.warn('AudioEngine not initialized');
      return;
    }

    if (this.context.state === 'suspended') {
      this.context.resume().catch(() => {});
    }

    const now = this.context.currentTime;

    // Create oscillator for count-in kick (more pronounced)
    const oscillator = this.context.createOscillator();
    oscillator.type = 'sine';
    
    // Pitch bend from 180Hz to 50Hz for stronger punch
    oscillator.frequency.setValueAtTime(180, now);
    oscillator.frequency.exponentialRampToValueAtTime(50, now + 0.08);

    // Create gain envelope (LOUDER for count-in)
    const gainNode = this.context.createGain();
    gainNode.gain.setValueAtTime(1.2, now); // ← MORE VOLUME than 0.8
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.25); // ← LONGER than 0.15

    // Connect and play
    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain);

    oscillator.start(now);
    oscillator.stop(now + 0.25);

    // Clean up
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  }

  /**
   * Play snare (caja) sound - bright, snappy percussion
   * Combination of tone and noise for snap
   */
  playSnare(): void {
    if (!this.context || !this.masterGain) {
      console.warn('AudioEngine not initialized');
      return;
    }

    if (this.context.state === 'suspended') {
      this.context.resume().catch(() => {});
    }

    const now = this.context.currentTime;

    // Tonal component (220 Hz)
    const toneOsc = this.context.createOscillator();
    toneOsc.type = 'triangle';
    toneOsc.frequency.value = 220;

    const toneGain = this.context.createGain();
    toneGain.gain.setValueAtTime(0.4, now);
    toneGain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

    toneOsc.connect(toneGain);
    toneGain.connect(this.masterGain);

    // Noise component for snap
    const bufferSize = this.context.sampleRate * 0.2;
    const buffer = this.context.createBuffer(1, bufferSize, this.context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noiseSource = this.context.createBufferSource();
    noiseSource.buffer = buffer;

    const noiseFilter = this.context.createBiquadFilter();
    noiseFilter.type = 'highpass';
    noiseFilter.frequency.value = 1000;

    const noiseGain = this.context.createGain();
    noiseGain.gain.setValueAtTime(0.6, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);

    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.masterGain);

    // Start both components
    toneOsc.start(now);
    toneOsc.stop(now + 0.1);
    noiseSource.start(now);
    noiseSource.stop(now + 0.12);

    // Clean up
    const cleanup = () => {
      toneOsc.disconnect();
      toneGain.disconnect();
      noiseSource.disconnect();
      noiseFilter.disconnect();
      noiseGain.disconnect();
    };
    
    toneOsc.onended = cleanup;
  }

  /**
   * Start rhythm loop: kick every 500ms, snare every 2000ms
   * Returns stop function
   */
  startRhythmLoop(): () => void {
    this.stopRhythmLoop(); // Clear any existing loop

    const BEAT_INTERVAL = 500; // 120 BPM
    let beatCount = 0;

    // Schedule beats
    const scheduleBeats = () => {
      // Kick on every beat
      const kickId = window.setTimeout(() => {
        this.playKick();
        
        // Snare every 4 beats
        if (beatCount % 4 === 0) {
          this.playSnare();
        }
        
        beatCount++;
        this.kickScheduleIds.push(scheduleBeats());
      }, BEAT_INTERVAL);
      
      return kickId;
    };

    // Start first beat immediately
    this.playKick();
    this.playSnare();
    beatCount = 1;
    this.kickScheduleIds.push(scheduleBeats());

    return () => this.stopRhythmLoop();
  }

  /**
   * Stop rhythm loop
   */
  stopRhythmLoop(): void {
    this.kickScheduleIds.forEach(id => clearTimeout(id));
    this.snareScheduleIds.forEach(id => clearTimeout(id));
    this.kickScheduleIds = [];
    this.snareScheduleIds = [];
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.stopRhythmLoop();
    if (this.context) {
      this.context.close();
      this.context = null;
      this.masterGain = null;
      this.isInitialized = false;
    }
  }
}
